import { makeAutoObservable, runInAction } from 'mobx';
import {
  type ComputerReleaseDraft,
  type ComputerReleaseDraftSummary,
  type ComputerReleaseNotesApi,
  computerReleaseDraftErrorMessage,
} from '../services/computerReleaseNotesClient.ts';

export class ComputerReleaseDraftStore {
  drafts: ComputerReleaseDraftSummary[] = [];
  nextPageToken = '';
  loading = false;
  listError = '';
  selectedVersion = '';
  detail: ComputerReleaseDraft | null = null;
  detailLoading = false;
  detailError = '';
  private api: ComputerReleaseNotesApi;
  private listRequest: AbortController | null = null;
  private detailRequest: AbortController | null = null;

  constructor(api: ComputerReleaseNotesApi) {
    this.api = api;
    makeAutoObservable<this, 'api' | 'listRequest' | 'detailRequest'>(this, {
      api: false,
      listRequest: false,
      detailRequest: false,
    });
  }

  refresh() {
    return this.loadList('');
  }

  async loadMore() {
    if (!this.loading && this.nextPageToken)
      await this.loadList(this.nextPageToken);
  }

  private async loadList(pageToken: string) {
    this.listRequest?.abort();
    const request = new AbortController();
    this.listRequest = request;
    this.loading = true;
    this.listError = '';
    try {
      const result = await this.api.list(pageToken, request.signal);
      runInAction(() => {
        if (this.listRequest !== request || request.signal.aborted) return;
        this.drafts = pageToken
          ? [...this.drafts, ...result.drafts]
          : result.drafts;
        this.nextPageToken = result.nextPageToken;
      });
    } catch (error) {
      runInAction(() => {
        if (this.listRequest === request && !request.signal.aborted) {
          this.listError = computerReleaseDraftErrorMessage(error);
        }
      });
    } finally {
      runInAction(() => {
        if (this.listRequest === request) {
          this.listRequest = null;
          this.loading = false;
        }
      });
    }
  }

  async select(version: string) {
    this.detailRequest?.abort();
    const request = new AbortController();
    this.detailRequest = request;
    this.selectedVersion = version;
    this.detail = null;
    this.detailLoading = true;
    this.detailError = '';
    try {
      const detail = await this.api.get(version, request.signal);
      runInAction(() => {
        if (this.detailRequest === request && !request.signal.aborted)
          this.detail = detail;
      });
    } catch (error) {
      runInAction(() => {
        if (this.detailRequest === request && !request.signal.aborted) {
          this.detailError = computerReleaseDraftErrorMessage(error);
        }
      });
    } finally {
      runInAction(() => {
        if (this.detailRequest === request) {
          this.detailRequest = null;
          this.detailLoading = false;
        }
      });
    }
  }

  applyUpdatedDraft(draft: ComputerReleaseDraft) {
    const {
      bodyMarkdown: _body,
      sourceSummary: _source,
      baseSha: _base,
      editorial,
      ...metadata
    } = draft;
    const summary: ComputerReleaseDraftSummary = {
      ...metadata,
      editorial: editorial ? { ...editorial } : undefined,
    };
    this.drafts = this.drafts.map((row) =>
      row.version === draft.version ? summary : row,
    );
    if (this.selectedVersion === draft.version) this.detail = draft;
  }

  dispose() {
    this.listRequest?.abort();
    this.detailRequest?.abort();
    this.listRequest = null;
    this.detailRequest = null;
    this.loading = false;
    this.detailLoading = false;
  }
}
